import { useEffect } from 'react';
import { Flex, Form, Input, Select } from 'antd';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import Callout from '@/components/callout/callout';
import { FormModal } from '@/components/form-modal/form-modal';
import { usePlaceTestCall } from '@/hooks/use-outbound-calls/use-outbound-calls';
import {
  TEST_CALL_ERROR_CODES,
  TestCallErrorCode,
} from '@/services/outbound-calls-service/calls.service.types';
import { OutboundScenario } from '@/services/outbound-calls-service/scenarios.service.types';
import { getApiErrorMessage } from '@/utils/api-error/api-error';
import { languageName } from '@/utils/language-name';
import { openNotification } from '@/utils/notification/notification';
import { DEFAULT_PHONE_PREFIX, phoneRule } from '@/utils/phone';
import { outboundErrorMessage } from '../outbound-calls.messages';
import { formFieldErrors, preferredLanguage } from '../outbound-calls.utils';

type TestCallValues = { phone: string; language: string; opening_line?: string };

const FIELDS = ['phone', 'language', 'opening_line'] as const;

const errorCode = (error: unknown): TestCallErrorCode | undefined => {
  const code: unknown = isAxiosError(error) ? error.response?.data?.code : undefined;
  return TEST_CALL_ERROR_CODES.find((known) => known === code);
};

interface TestCallModalProps {
  scenario?: OutboundScenario;
  onClose: () => void;
}

/** Calls one number right now with the scenario, outside any campaign. */
export function TestCallModal({ scenario, onClose }: TestCallModalProps) {
  const { t } = useTranslation(['outbound', 'common']);
  const [form] = Form.useForm<TestCallValues>();
  const { placeTestCall, isPending } = usePlaceTestCall();
  const languages = scenario?.languages ?? [];

  useEffect(() => {
    if (!scenario) return;
    form.resetFields();
    form.setFieldsValue({ language: preferredLanguage(scenario.languages) });
  }, [scenario, form]);

  const handleSubmit = async (values: TestCallValues) => {
    if (!scenario) return;
    try {
      await placeTestCall({
        outbound_call_scenario_id: scenario.id,
        phone: values.phone.trim(),
        language: values.language,
        opening_line: values.opening_line?.trim() || undefined,
      });
      openNotification({ type: 'success', message: t('outbound:testCall.placed') });
      onClose();
    } catch (error) {
      const fields = formFieldErrors(error, FIELDS);
      if (fields.length) {
        form.setFields(fields);
        return;
      }
      // The code names the failure; the provider's own words, if any, say which number.
      const code = errorCode(error);
      const detail = code === 'twilio_rejected' || code === 'outbound_rejected';
      openNotification({
        type: 'error',
        message: code
          ? [t(`outbound:testCall.errors.${code}`), detail && getApiErrorMessage(error, '')]
              .filter(Boolean)
              .join(': ')
          : outboundErrorMessage(error, t('outbound:actionFailed')),
      });
    }
  };

  return (
    <FormModal
      open={!!scenario}
      title={t('outbound:testCall.title')}
      subtitle={t('outbound:testCall.subtitle', { name: scenario?.name ?? '' })}
      submitLabel={t('outbound:testCall.call')}
      onSubmit={() => form.submit()}
      onCancel={onClose}
      isLoading={isPending}
    >
      <Flex vertical gap={16}>
        <Callout tone="strong">{t('outbound:testCall.warning')}</Callout>
        <Form<TestCallValues>
          form={form}
          name="testCall"
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ phone: DEFAULT_PHONE_PREFIX }}
        >
          <Form.Item
            label={t('outbound:testCall.phone')}
            name="phone"
            rules={[
              { required: true, message: t('outbound:testCall.phoneRequired') },
              phoneRule(t('outbound:testCall.phoneInvalid')),
            ]}
          >
            <Input placeholder="+48 123 456 789" />
          </Form.Item>
          <Form.Item
            label={t('outbound:testCall.language')}
            name="language"
            rules={[{ required: true, message: t('outbound:fieldError.required') }]}
          >
            <Select
              options={languages.map((code) => ({ value: code, label: languageName(code) }))}
            />
          </Form.Item>
          <Form.Item label={t('outbound:testCall.openingLine')} name="opening_line">
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder={scenario?.opening_line ?? ''}
            />
          </Form.Item>
        </Form>
      </Flex>
    </FormModal>
  );
}
